// Rock Sorter and Layer Dig. Sort rocks into their three families, then dig through
// layers where deeper is older. Fossils found become charms.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { shuffle, pick } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { FAMILY_INFO, FOSSILS, rocksForLevel, clueDepth, type Family, type Rock } from '@content/science/rocks';

const ROCKS_PER_ROUND = 8;
const LAYERS = 7;

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const depth = clueDepth(level);
  const rocks = shuffle(ctx.rng, rocksForLevel(level)).slice(0, ROCKS_PER_ROUND);
  let i = 0;
  let attempts = 0, correct = 0, streak = 0;
  let igneousAllRight = true;
  let missedThis = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Rock ', h('strong', null, '1'), ` of ${rocks.length}`), h('span.muted.small', null, ''));
  const stage = h('div');
  const name = h('div.prompt');
  const clue = h('p.center', { style: { minHeight: '48px', fontWeight: '700' } });
  const bins = h('div.stack', null, ...(['igneous', 'sedimentary', 'metamorphic'] as Family[]).map(f =>
    h('button.btn.block', { onclick: () => choose(f), style: { borderColor: FAMILY_INFO[f].color, justifyContent: 'space-between' } },
      h('span', null, FAMILY_INFO[f].label), h('span.muted.small', null, FAMILY_INFO[f].kid)),
  ));
  host.append(head, stage, name, clue, bins);

  const S = makeSurface(stage, { aspect: 0.5, draw: false, onResize: () => drawRock() });

  function cur(): Rock { return rocks[i]; }

  function show() {
    (head.querySelector('strong') as HTMLElement).textContent = String(i + 1);
    (head.lastElementChild as HTMLElement).textContent = depth === 0 ? 'Look closely' : depth === 1 ? 'How did it form?' : 'Think it through';
    name.textContent = cur().name;
    clue.textContent = cur().clues[depth];
    missedThis = false;
    tProblem = performance.now();
    drawRock();
  }

  function choose(f: Family) {
    const r = cur();
    const ms = performance.now() - tProblem;
    if (f === r.family) {
      if (!missedThis) { attempts++; correct++; streak++; ctx.report({ skill: 'geology', level, correct: true, hintUsed: false, ms, problemId: `rock:${r.name}` }); }
      ctx.sound('ding');
      ctx.say(`${r.name} is ${FAMILY_INFO[f].label.toLowerCase()}: ${FAMILY_INFO[f].how}.`);
      if (r.name === 'Obsidian') ctx.speak('Obsidian');
      i++;
      if (i >= rocks.length) { setTimeout(dig, 900); return; }
      setTimeout(show, 1000);
    } else {
      if (!missedThis) { attempts++; streak = 0; missedThis = true; if (r.family === 'igneous') igneousAllRight = false; ctx.report({ skill: 'geology', level, correct: false, hintUsed: false, ms, problemId: `rock:${r.name}` }); }
      ctx.sound('buzz');
      const right = FAMILY_INFO[r.family];
      ctx.say(`Not ${FAMILY_INFO[f].label.toLowerCase()}. Hint: this rock was ${right.how}. ${r.from ? `It used to be ${r.from}.` : ''}`);
    }
  }

  function drawRock() {
    const c = S.ctx, W = S.w, H = S.h;
    const r = cur();
    if (!r) return;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    const cx = W / 2, cy = H / 2, rw = Math.min(W * 0.5, H * 1.4) / 2, rh = H * 0.36;
    // blob
    c.fillStyle = r.color;
    c.beginPath();
    c.moveTo(cx - rw, cy + rh * 0.2); c.lineTo(cx - rw * 0.7, cy - rh * 0.7); c.lineTo(cx - rw * 0.1, cy - rh); c.lineTo(cx + rw * 0.6, cy - rh * 0.8); c.lineTo(cx + rw, cy - rh * 0.1); c.lineTo(cx + rw * 0.75, cy + rh * 0.8); c.lineTo(cx - rw * 0.5, cy + rh); c.closePath(); c.fill();
    c.save(); c.clip();
    const dark = 'rgba(0,0,0,.22)', light = 'rgba(255,255,255,.35)';
    switch (r.look) {
      case 'bubbly': for (let k = 0; k < 40; k++) { c.fillStyle = dark; c.beginPath(); c.arc(cx - rw + ctx.rng() * rw * 2, cy - rh + ctx.rng() * rh * 2, 2 + ctx.rng() * 4, 0, 7); c.fill(); } break;
      case 'glassy': c.fillStyle = light; c.beginPath(); c.moveTo(cx - rw * 0.5, cy - rh * 0.6); c.lineTo(cx + rw * 0.2, cy - rh * 0.9); c.lineTo(cx + rw * 0.4, cy - rh * 0.3); c.closePath(); c.fill(); c.fillStyle = dark; c.beginPath(); c.moveTo(cx + rw * 0.2, cy + rh * 0.3); c.lineTo(cx + rw * 0.7, cy + rh * 0.1); c.lineTo(cx + rw * 0.3, cy + rh * 0.8); c.closePath(); c.fill(); break;
      case 'speckled': for (let k = 0; k < 90; k++) { c.fillStyle = ['#2a2a2e', '#f6f3ec', '#ee86b7', '#9a9aa3'][k % 4]; c.fillRect(cx - rw + ctx.rng() * rw * 2, cy - rh + ctx.rng() * rh * 2, 3, 3); } break;
      case 'banded': for (let k = -4; k <= 4; k++) { c.fillStyle = k % 2 ? dark : light; c.beginPath(); const yy = cy + k * rh * 0.22; c.moveTo(cx - rw, yy); c.quadraticCurveTo(cx, yy + 10, cx + rw, yy - 6); c.lineTo(cx + rw, yy + rh * 0.1); c.quadraticCurveTo(cx, yy + rh * 0.1 + 10, cx - rw, yy + rh * 0.1); c.closePath(); c.fill(); } break;
      case 'grainy': for (let k = 0; k < 160; k++) { c.fillStyle = k % 3 ? dark : light; c.fillRect(cx - rw + ctx.rng() * rw * 2, cy - rh + ctx.rng() * rh * 2, 2, 2); } for (let k = -2; k <= 2; k++) { c.fillStyle = 'rgba(0,0,0,.08)'; c.fillRect(cx - rw, cy + k * rh * 0.35, rw * 2, 3); } break;
      case 'pebbly': for (let k = 0; k < 18; k++) { c.fillStyle = ['#9a9aa3', '#8a5a3b', '#f6f3ec', '#b48a5a'][k % 4]; c.beginPath(); c.ellipse(cx - rw + ctx.rng() * rw * 2, cy - rh + ctx.rng() * rh * 2, 6 + ctx.rng() * 6, 4 + ctx.rng() * 5, ctx.rng() * 3, 0, 7); c.fill(); } break;
      case 'wavy': c.strokeStyle = 'rgba(0,0,0,.15)'; c.lineWidth = 3; for (let k = -3; k <= 3; k++) { c.beginPath(); const yy = cy + k * rh * 0.28; c.moveTo(cx - rw, yy); c.bezierCurveTo(cx - rw * 0.3, yy - 14, cx + rw * 0.3, yy + 14, cx + rw, yy); c.stroke(); } break;
      case 'shiny': for (let k = 0; k < 30; k++) { c.fillStyle = light; const px = cx - rw + ctx.rng() * rw * 2, py = cy - rh + ctx.rng() * rh * 2; c.beginPath(); c.moveTo(px, py - 5); c.lineTo(px + 3, py); c.lineTo(px, py + 5); c.lineTo(px - 3, py); c.closePath(); c.fill(); } break;
      case 'flaky': for (let k = -4; k <= 4; k++) { c.fillStyle = k % 2 ? dark : 'rgba(0,0,0,.05)'; c.fillRect(cx - rw, cy + k * rh * 0.2, rw * 2, rh * 0.08); } break;
      case 'chalky': c.fillStyle = 'rgba(0,0,0,.06)'; for (let k = 0; k < 12; k++) { c.beginPath(); c.arc(cx - rw + ctx.rng() * rw * 2, cy - rh + ctx.rng() * rh * 2, 5 + ctx.rng() * 8, 0, 7); c.fill(); } c.strokeStyle = 'rgba(0,0,0,.18)'; c.lineWidth = 1.5; for (let k = 0; k < 4; k++) { const px = cx - rw * 0.6 + k * rw * 0.4, py = cy + (k % 2 ? -1 : 1) * rh * 0.3; c.beginPath(); for (let t = 0; t < 10; t += 0.3) c.lineTo(px + Math.cos(t) * t * 0.8, py + Math.sin(t) * t * 0.8); c.stroke(); } break;
    }
    c.restore();
  }

  // ---- Layer Dig ----
  function dig() {
    S.destroy();
    clear(host);
    ctx.say('');
    const layers = LAYERS;
    let dug = 0;
    const foundIds: string[] = [];
    const head2 = h('h3.center', null, 'Layer Dig');
    const info = h('p.center.muted.small', null, 'Each layer took thousands of years to settle. Tap Dig to go down.');
    const stage2 = h('div');
    const digBtn = h('button.btn.primary.block', { onclick: doDig }, 'Dig');
    host.append(head2, info, stage2, digBtn);
    const S2 = makeSurface(stage2, { aspect: 0.9, draw: false, onResize: drawLayers });
    const cols = ['#c9a87c', '#e0b07a', '#b9a99a', '#8c8a86', '#d9b98a', '#a0622d', '#5b6470'];

    function drawLayers() {
      const c = S2.ctx, W = S2.w, H = S2.h;
      c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H);
      const lh = H / (layers + 0.6);
      for (let k = 0; k < layers; k++) {
        const y = H - (layers - k) * lh;
        c.fillStyle = k < dug ? '#efe9db' : cols[k];
        c.fillRect(0, y, W, lh + 1);
        if (k < dug) { c.fillStyle = 'rgba(0,0,0,.06)'; c.fillRect(0, y, W, lh + 1); }
        c.fillStyle = k < dug ? '#9a9aa3' : 'rgba(0,0,0,.45)';
        c.font = `800 ${Math.round(lh * 0.35)}px Nunito, sans-serif`; c.textAlign = 'left'; c.textBaseline = 'middle';
        c.fillText(k === 0 ? 'Layer 1 (newest)' : k === layers - 1 ? `Layer ${k + 1} (oldest)` : `Layer ${k + 1}`, 10, y + lh / 2);
        const f = FOSSILS.find(x => x.depth === k + 1);
        if (f && k < dug) { c.fillStyle = f.color; c.beginPath(); c.arc(W * 0.86, y + lh / 2, lh * 0.3, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.font = `700 ${Math.round(lh * 0.3)}px Nunito, sans-serif`; c.textAlign = 'right'; c.fillText(f.name, W * 0.86 - lh * 0.42, y + lh / 2); }
      }
      // grass on top
      c.fillStyle = '#9ad67e'; c.fillRect(0, H - layers * lh - 8, W, 8);
    }

    function doDig() {
      if (dug >= layers) return;
      dug++;
      ctx.sound('pop');
      const f = FOSSILS.find(x => x.depth === dug);
      if (f) { foundIds.push(f.id); ctx.charm(f.charm); info.textContent = `A ${f.name.toLowerCase()}! ${f.fact}`; ctx.sound('chime'); }
      else info.textContent = dug === layers ? 'Bedrock. You reached the bottom.' : `Layer ${dug} cleared. Deeper is older.`;
      drawLayers();
      if (dug >= layers) { digBtn.remove(); setTimeout(question, 600); }
    }

    function question() {
      const qs: { q: string; opts: string[]; a: number; why: string }[] = [
        { q: 'Which layer is the oldest?', opts: ['Layer 1, at the top', `Layer ${layers}, at the bottom`], a: 1, why: 'New layers pile on top of old ones, so the bottom is oldest.' },
        { q: 'Which fossil is older?', opts: ['The fern (layer 2)', 'The trilobite (layer 4)'], a: 1, why: 'The trilobite was deeper, and deeper layers were buried first.' },
        { q: 'A layer has lots of sea shells in it. What was this place long ago?', opts: ['A desert', 'Under the sea'], a: 1, why: 'Shells settle on the sea floor, so that layer formed under water.' },
        { q: 'Which layer took the longest to be buried?', opts: [`Layer ${layers} at the bottom`, 'Layer 1 at the top'], a: 0, why: 'Every layer above it had to pile on after it formed.' },
      ];
      const q = level <= 3 ? qs[0] : pick(ctx.rng, qs);
      const tq = performance.now();
      const qa = h('div.stack', null,
        h('h3.center', null, q.q),
        ...q.opts.map((o, k) => h('button.btn.block', { onclick: () => answer(k) }, o)),
      );
      host.append(qa);
      let answered = false;
      function answer(k: number) {
        if (answered) return;
        answered = true;
        attempts++;
        const ok = k === q.a;
        if (ok) { correct++; ctx.sound('ding'); ctx.say(`Right. ${q.why}`); }
        else { ctx.sound('buzz'); ctx.say(`Not quite. ${q.why}`); }
        ctx.report({ skill: 'geology', level, correct: ok, hintUsed: false, ms: performance.now() - tq, problemId: 'dig:q' });
        if (correct === attempts) ctx.charm('charm.geode');
        if (igneousAllRight && rocks.some(r => r.family === 'igneous')) ctx.charm('charm.obsidian');
        setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: foundIds.length ? [`Fossils found: ${foundIds.length}`] : [] }), 1500);
      }
    }
    void streak;
  }

  show();
  return { destroy() { try { S.destroy(); } catch { /* already gone */ } clear(host); } };
}

const mod: GameModule = {
  id: 'science.rock-sorter',
  title: 'Rock Sorter',
  blurb: 'Sort rocks into fire, layer and squeezed families, then dig for fossils.',
  category: 'science',
  skills: ['geology'],
  levelRange: [1, 10],
  unlock: { stars: 6 },
  mount,
};
export default mod;
